import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNotification } from '../../contexts/NotificationContext'
import { compareDivisions } from '../../lib/divisionOrder'
import {
  fetchBracketsForTournament,
  fetchGroups as fetchGroupsApi,
  fetchTournaments,
  upsertPrize,
} from '../../services/prizeService'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import TypeAheadSearch from '../ui/type-ahead-search'

export default function PrizeFormModal({ prize, onClose, onSave }) {
  const [formData, setFormData] = useState({
    tournament_id: prize?.tournament_id || '',
    bracket_id: prize?.bracket_id || '',
    group_id: prize?.group_id || '',
    name: prize?.name || '',
    description: prize?.description || '',
    place: prize?.place || 1,
    promo_code: prize?.promo_code || '',
    redeem_url: prize?.redeem_url || '',
    combined_redeem_url: prize?.combined_redeem_url || '',
  })
  const [saving, setSaving] = useState(false)
  const [, setTournaments] = useState([])
  const [groups, setGroups] = useState([])
  const [brackets, setBrackets] = useState([])
  const [selectedTournament, setSelectedTournament] = useState(null)
  const notify = useNotification()

  // Fetch tournaments based on selected group
  useEffect(() => {
    async function loadTournaments() {
      try {
        const data = await fetchTournaments({
          p_group_id: formData.group_id || null
        })
        if (data) {
          setTournaments(data.map(t => ({ id: t.id, name: t.name })))
        }
      } catch (err) {
        console.error('Error fetching tournaments:', err)
      }
    }
    loadTournaments()
  }, [formData.group_id])

  // Fetch groups
  useEffect(() => {
    async function loadGroups() {
      try {
        const data = await fetchGroupsApi()
        if (data) {
          setGroups(data)
        }
      } catch (err) {
        console.error('Error fetching groups:', err)
      }
    }
    loadGroups()
  }, [])

  // Fetch brackets when tournament is selected
  useEffect(() => {
    async function loadBrackets() {
      if (!formData.tournament_id) {
        setBrackets([])
        return
      }
      try {
        const data = await fetchBracketsForTournament(formData.tournament_id)
        if (data) {
          setBrackets(
            data.map(b => ({
              id: b.id,
              name: b.divisions?.name || 'Unknown Division',
              gender: b.divisions?.gender,
              team_size: b.divisions?.team_size,
            })).sort(compareDivisions)
          )
        }
      } catch (err) {
        console.error('Error fetching brackets:', err)
      }
    }
    loadBrackets()
  }, [formData.tournament_id])

  const searchTournamentsInForm = async (query) => {
    try {
      const data = await fetchTournaments({
        p_search_term: query || null,
        p_group_id: formData.group_id || null
      })
      if (data) {
        return data.map(t => ({ id: t.id, name: t.name })).slice(0, 10)
      }
      return []
    } catch (error) {
      console.error('Error searching tournaments:', error)
      return []
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.tournament_id) {
      notify.error('Please select a tournament')
      return
    }
    if (!formData.name.trim()) {
      notify.error('Please enter a prize name')
      return
    }

    setSaving(true)
    try {
      const data = await upsertPrize({
        p_id: prize?.id || null,
        p_tournament_id: formData.tournament_id,
        p_bracket_id: formData.bracket_id || null,
        p_group_id: formData.group_id || null,
        p_name: formData.name.trim(),
        p_description: formData.description.trim() || null,
        p_place: formData.place,
        p_promo_code: formData.promo_code.trim() || null,
        p_redeem_url: formData.redeem_url.trim() || null,
        p_combined_redeem_url: formData.combined_redeem_url.trim() || null,
      })

      if (!data.success) throw new Error(data.error)

      notify.success(prize ? 'Prize updated!' : 'Prize created!')
      onSave()
      onClose()
    } catch (err) {
      console.error('Error saving prize:', err)
      notify.error(err.message || 'Failed to save prize')
    } finally {
      setSaving(false)
    }
  }

  const getPlaceLabel = (place) => {
    switch (place) {
      case 1: return '1st Place'
      case 2: return '2nd Place'
      case 3: return '3rd Place'
      default: return `${place}th Place`
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{prize ? 'Edit Prize' : 'Create New Prize'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Group</label>
            <Select
              value={formData.group_id || undefined}
              onValueChange={(nextGroupId) => {
                setFormData((prev) => ({
                  ...prev,
                  group_id: nextGroupId
                }))
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Global Prize (All Users)" />
              </SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Leave as "Global Prize" for prizes available to all users
            </p>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Tournament *</label>
            <TypeAheadSearch
              value={formData.tournament_id}
              valueLabel={selectedTournament?.name || prize?.tournament?.name || ''}
              onChange={(tournament) => {
                if (tournament) {
                  setSelectedTournament(tournament)
                  setFormData({ ...formData, tournament_id: tournament.id, bracket_id: '' })
                } else {
                  setSelectedTournament(null)
                  setFormData({ ...formData, tournament_id: '', bracket_id: '' })
                }
              }}
              onSearch={searchTournamentsInForm}
              getItemLabel={(t) => t.name}
              getItemValue={(t) => t.id}
              placeholder="Search for a tournament..."
              emptyMessage="No tournaments found"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Bracket (Optional)</label>
            <Select
              value={formData.bracket_id || undefined}
              onValueChange={(val) => setFormData({ ...formData, bracket_id: val })}
              disabled={!formData.tournament_id}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Tournament-level prize (all brackets)" />
              </SelectTrigger>
              <SelectContent>
                {brackets.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Leave empty for tournament-level prize, or select a specific bracket
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Prize Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="$50 Gift Card"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Place</label>
              <Select
                value={formData.place.toString()}
                onValueChange={(val) => setFormData({ ...formData, place: parseInt(val) })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select place" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((p) => (
                    <SelectItem key={p} value={p.toString()}>{getPlaceLabel(p)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Description</label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description..."
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Promo Code</label>
            <Input
              value={formData.promo_code}
              onChange={(e) => setFormData({ ...formData, promo_code: e.target.value })}
              placeholder="SAVE50 or gift card code"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Redeem URL</label>
            <Input
              value={formData.redeem_url}
              onChange={(e) => setFormData({ ...formData, redeem_url: e.target.value })}
              placeholder="https://store.example.com"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Combined URL (Optional)</label>
            <Input
              value={formData.combined_redeem_url}
              onChange={(e) => setFormData({ ...formData, combined_redeem_url: e.target.value })}
              placeholder="https://store.example.com?code=SAVE50"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : prize ? 'Update Prize' : 'Create Prize'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
